import {
  Criteria,
  PasswordStrength,
  passwordCriteriaValidators,
  LocalizedString,
} from "@dbn/omnichannel-design-system-g4p";
import { useFormatMessage } from "../intl";
// eslint-disable-next-line no-restricted-imports
import { CriteriaValidator } from "@dbn/omnichannel-design-system-g4p/lib/PasswordFieldWithCriteria/criteriaValidators";
import { eq, task, taskEither } from "fp-ts";
import { useCommand } from "../useAPI";
import { apiCall } from "../APICall";
import { constFalse, pipe } from "fp-ts/function";
import * as t from "io-ts";

export const useCriteria = (onMobileFlow?: boolean) => {
  const formatMessage = useFormatMessage();

  const lengthRange: (
    minLength: number,
    maxLength: number
  ) => Criteria<string> = (minLength, maxLength) => ({
    label: formatMessage("Form.passwordCriteria.lengthRange", {
      minLength,
      maxLength,
    }),
    validator: passwordCriteriaValidators.lengthRange(minLength, maxLength),
  });

  const minLength: (length: number) => Criteria<string> = length => ({
    label: formatMessage("Form.passwordCriteria.minLength", { length }),
    validator: passwordCriteriaValidators.minLength(length),
  });

  const passwordMutations = [
    "password",
    "p@ssword",
    "passw0rd",
    "passwd",
    "pass123",
    "1234",
    "pass",
    "word",
  ];

  const containsPasswordMutation: CriteriaValidator<string> = (
    input: string
  ) => {
    const lowerInput = input.toLowerCase();
    const found = passwordMutations.some(mutation =>
      lowerInput.includes(mutation)
    );
    return task.of(!found);
  };

  const hasPasswordMutations: Criteria<string> = {
    label: formatMessage("Form.passwordCriteria.containsPasswordMutation"),
    validator: containsPasswordMutation,
  };

  const PasswordInput = t.type(
    {
      password: t.string,
    },
    "PasswordInput"
  );

  const checkGuessablePasswordApi = apiCall({
    path: ["clients", "profile", "passwordContainsGuessableData"],
    inputEq: eq.fromEquals(constFalse),
    inputCodec: PasswordInput,
    outputCodec: t.boolean,
  });

  const checkGuessablePasswordMobileFlowsApi = apiCall({
    path: [
      "clients",
      "identification",
      "mobileCreateProfile",
      "credentials",
      "passwordContainsGuessableData",
    ],
    inputEq: eq.fromEquals(constFalse),
    inputCodec: PasswordInput,
    outputCodec: t.boolean,
  });

  const checkGuessablePassword = useCommand(
    onMobileFlow
      ? checkGuessablePasswordMobileFlowsApi
      : checkGuessablePasswordApi
  );

  const containsGuessableData: CriteriaValidator<string> = (input: string) =>
    pipe(
      checkGuessablePassword({ password: input.toLowerCase() }),
      taskEither.fold(
        () => task.of(false),
        response => task.of(!response)
      )
    );

  const hasGuessableData: Criteria<string> = {
    label: formatMessage("Form.passwordCriteria.containsGuessableData"),
    validator: containsGuessableData,
  };

  const hasDigit: Criteria<string> = {
    label: formatMessage("Form.passwordCriteria.digit"),
    validator: passwordCriteriaValidators.hasDigit,
  };

  const hasSpecialCharacter: Criteria<string> = {
    label: formatMessage("Form.passwordCriteria.specialCharacter"),
    validator: passwordCriteriaValidators.hasSpecialCharacter,
  };

  const hasLowerLetter: Criteria<string> = {
    label: formatMessage("Form.passwordCriteria.lowercaseLetter"),
    validator: passwordCriteriaValidators.hasLowerLetter,
  };

  const hasCapitalLetter: Criteria<string> = {
    label: formatMessage("Form.passwordCriteria.capitalLetter"),
    validator: passwordCriteriaValidators.hasCapitalLetter,
  };

  const noPartialConsecutiveDigits: Criteria<string> = {
    label: formatMessage("Form.passwordCriteria.noConsecutiveDigits"),
    validator: passwordCriteriaValidators.noPartialConsecutiveDigits,
  };

  const noDigitRepeatsMoreThanN: (
    maxRepetitions: number
  ) => Criteria<string> = maxRepetitions => ({
    label: formatMessage("Form.passwordCriteria.noDigitRepeatsMoreThanN", {
      times: maxRepetitions,
    }),
    validator: passwordCriteriaValidators.noDigitRepeatsMoreThanN(
      maxRepetitions
    ),
  });

  const noSpecialCharacters: Criteria<string> = {
    label: formatMessage("Form.passwordCriteria.noSpecialCharacters"),
    validator: passwordCriteriaValidators.noSpecialCharacters,
  };

  const noDigitFirstCharacter: Criteria<string> = {
    label: formatMessage("Form.passwordCriteria.noDigitFirstCharacter"),
    validator: passwordCriteriaValidators.noDigitFirstCharacter,
  };

  const clientPasswordCriteria = {
    lengthRange: lengthRange(8, 14),
    noDigitFirstCharacter,
    noSpecialCharacters,
    hasCapitalLetter,
    hasLowerLetter,
    hasDigit,
    hasPasswordMutations,
    hasGuessableData,
  };

  const thirdPartyPasswordCriteria = {
    minLength: minLength(8),
    hasDigit,
    hasLowerLetter,
    hasCapitalLetter,
    hasSpecialCharacter,
  } as const;

  const securityPinCriteria = {
    lengthRange: lengthRange(6, 8),
    noPartialConsecutiveDigits,
    noDigitRepeatsMoreThanN: noDigitRepeatsMoreThanN(2),
  };

  const passwordStrengthLabel = (
    strength: Exclude<PasswordStrength, "weak">
  ): LocalizedString => {
    switch (strength) {
      case "good":
        return formatMessage("Form.passwordField.goodPassword");
      case "strong":
        return formatMessage("Form.passwordField.strongPassword");
    }
  };

  const pinStrengthLabel = (
    strength: Exclude<PasswordStrength, "weak">
  ): LocalizedString => {
    switch (strength) {
      case "good":
        return formatMessage("Form.passwordField.goodPin");
      case "strong":
        return formatMessage("Form.passwordField.strongPin");
    }
  };

  return {
    clientPasswordCriteria,
    thirdPartyPasswordCriteria,
    securityPinCriteria,
    passwordStrengthLabel,
    pinStrengthLabel,
  };
};
